import { cn } from '@brew/ui/cn'
import { ArrowUpRight } from 'lucide-react'
import type { CSSProperties, MouseEvent, ReactNode } from 'react'

type CommonProps = {
  children: ReactNode
  className?: string
}

type AnchorProps = CommonProps & {
  href: string
  /**
   * Runs once hydrated (it may `preventDefault` to act in place, e.g. open a
   * modal); before that, or without JavaScript, the link simply navigates.
   */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
  type?: never
  target?: string
  rel?: string
}

type ButtonProps = CommonProps & {
  href?: never
  onClick: () => void
  type?: 'button' | 'submit'
  target?: never
  rel?: never
}

export type ProminentCtaButtonProps = AnchorProps | ButtonProps

const SHIMMER_STYLE = {
  '--spread': '90deg',
  '--shimmer-color': '#ffffff',
  '--radius': '0.75rem',
  '--speed': '3s',
  '--cut': '0.05em',
  '--bg': 'rgb(222, 91, 30)',
} as CSSProperties

const PROMINENT_CTA_CLASSES = cn(
  'group relative z-0 inline-flex h-11 cursor-pointer items-center justify-center gap-2 overflow-hidden whitespace-nowrap border border-white/10 px-6 font-medium text-sm text-white shadow-2xl [background:var(--bg)] [border-radius:var(--radius)]',
  'transform-gpu transition-transform duration-300 ease-in-out hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-primary/50 focus-visible:outline-offset-2 active:translate-y-px'
)

function ProminentCtaButtonContent({ children }: { children: ReactNode }) {
  return (
    <>
      <span className="absolute inset-0 -z-30 overflow-visible blur-[2px] [container-type:size]">
        <span className="absolute inset-0 h-[100cqh] animate-shimmer-slide [aspect-ratio:1] [border-radius:0] [mask:none]">
          <span className="absolute -inset-full w-auto rotate-0 animate-spin-around [background:conic-gradient(from_calc(270deg-(var(--spread)*0.5)),transparent_0,var(--shimmer-color)_var(--spread),transparent_var(--spread))]" />
        </span>
      </span>
      <span className="absolute inset-0 rounded-2xl shadow-[inset_0_-8px_10px_#ffffff1f] transition-all duration-300 ease-in-out group-hover:shadow-[inset_0_-6px_10px_#ffffff3f] group-active:shadow-[inset_0_-10px_10px_#ffffff3f]" />
      <span className="absolute -z-20 [background:var(--bg)] [border-radius:var(--radius)] [inset:var(--cut)]" />
      <span className="relative">{children}</span>
      <ArrowUpRight className="relative size-4" aria-hidden />
    </>
  )
}

export function ProminentCtaButton(props: ProminentCtaButtonProps) {
  const className = cn(PROMINENT_CTA_CLASSES, props.className)

  if (props.href !== undefined) {
    return (
      <a
        href={props.href}
        onClick={props.onClick}
        target={props.target}
        rel={props.rel}
        style={SHIMMER_STYLE}
        className={className}
      >
        <ProminentCtaButtonContent>{props.children}</ProminentCtaButtonContent>
      </a>
    )
  }

  return (
    <button
      type={props.type ?? 'button'}
      onClick={props.onClick}
      style={SHIMMER_STYLE}
      className={className}
    >
      <ProminentCtaButtonContent>{props.children}</ProminentCtaButtonContent>
    </button>
  )
}
